import React, { useEffect, useState } from 'react';
import { RenderProps } from './withProducts';
import { RenderProps as CategoryRenderProps } from 'rangeConfig/shared/widgets/withCategoryFilter';
import { Product } from '@inter-ikea-storageone/range';
import MobileDrawer from 'components/MobileDrawer/MobileDrawer';
import { ProductList } from './ProductList';
import MobileSwiper from 'components/MobileSwiper/MobileSwiper';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { CheckWidget } from './checkWidget';
import { getProductEntities } from 'ducks/core';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { useBehaviorTracker } from 'contexts/behaviors';

const ChildrenWrapper = styled.div`
  height: 100%;
  overflow: auto;
`;

type Props = RenderProps &
  CategoryRenderProps &
  CheckWidget & {
    id: string;
    title: string;
    selected?: boolean;
  };

const ProductSwiper = ({
  id,
  categoryIds,
  categoriesData,
  selectedGroupId,
  selectedGroup,
  title,
  selected,
  productsInGroup,
  hideTab,
  productListConfig
}: Props) => {
  const behaviorTracker = useBehaviorTracker();
  const { formatMessage } = useIntl();
  const nbrOfProductsInList = 10;
  // If current page is summary, we should hide the mobile drawer as that causes the summary page to not be usable on mobile devices
  const currentPageIsSummary = useLocation().pathname === AppRoutes.SUMMARY;

  const data = categoryIds
    ? categoriesData.filter(p => categoryIds.includes(p.id))
    : categoriesData;

  const categoryConfig = categoryIds
    ? productListConfig.categories.find(category => category.id === categoryIds[0])
    : productListConfig.categories[0];

  const isCluster = productListConfig.cluster && productListConfig.cluster.length > 0;

  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const products = data
    .reduce((acc, curr) => {
      return [...acc, ...curr.productsCluster];
    }, [] as Product[][])
    .filter(cluster => cluster.length > 0);

  const onSelect = (product: Product, listIndex: number) => {
    if (isCluster) {
      behaviorTracker.withMeta({ listIndex }).trigger(
        'productClicked',
        {
          targets: products.find(p => p.includes(product)) ?? []
        },
        productListConfig.behaviors
      );
    } else {
      behaviorTracker.withMeta({ listIndex }).trigger(
        'productClicked',
        {
          targets: [product]
        },
        productListConfig.behaviors
      );
    }
  };

  const getProductsToShow = (): Product[] => {
    if (isCluster) {
      return products?.map(p => p[0]);
    } else {
      let firstClusterProducts = products?.[0] || [];

      for (let i = 1; i < products.length; i++) {
        if (firstClusterProducts.length < nbrOfProductsInList) {
          firstClusterProducts = [...firstClusterProducts, ...products[i]];
        } else {
          break;
        }
      }

      return firstClusterProducts;
    }
  };

  const productsToShow = getProductsToShow();

  const showInfoButton =
    productListConfig.hideInfoButton !== undefined ? !productListConfig.hideInfoButton : true;

  const onAdditionalItemsClicked =
    productsToShow.length < nbrOfProductsInList && !showInfoButton
      ? undefined
      : () => {
          setShowMobileDrawer(true);
        };

  const productEntitiesInScene = useSelector(getProductEntities);

  const productsInSceneIds = productEntitiesInScene.map(p => p.productId);

  const groupHasFilters =
    selectedGroup?.mainMenu.filters && selectedGroup?.mainMenu.filters?.length > 0;

  useEffect(() => {
    if (hideTab && productsToShow.length === 0) {
      hideTab(true);
    } else if (hideTab) {
      hideTab(false);
    }
  }, [hideTab, productsToShow]);

  return (
    <>
      <MobileSwiper
        id={id}
        numberOfItems={nbrOfProductsInList}
        onSelect={onSelect}
        noneOption={categoryConfig?.noneOption}
        onAdditionalItemsClicked={onAdditionalItemsClicked}
        showInfoButton={showInfoButton}
        mainProductCardImage={productListConfig.productCardMainImage}
        selected={productListConfig.selected}
        selectedGroup={selectedGroup}
        selectedIds={productsInSceneIds}
        productClusters={isCluster ? products : productsToShow.map(p => [p])}
        mobileCardText={productListConfig.mobileCardText}
      />
      {selected && (
        <MobileDrawer
          open={showMobileDrawer && !currentPageIsSummary}
          title={title && formatMessage({ id: title })}
          onDismiss={() => setShowMobileDrawer(false)}
          showFilterButton={groupHasFilters}
        >
          <ChildrenWrapper>
            <ProductList
              categoriesData={data}
              selectedGroupId={selectedGroupId}
              selectedGroup={selectedGroup}
              productsInGroup={productsInGroup}
              productListConfig={productListConfig}
            />
          </ChildrenWrapper>
        </MobileDrawer>
      )}
    </>
  );
};
export default ProductSwiper;
